import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BookUser, MessageCircle, Phone, Share2, UserPlus, Video, X } from "lucide-react";
import { api, formatApiError } from "../api/client";
import { Avatar } from "../components/Avatar";
import { useCall } from "../context/CallContext";
import { ru } from "../locales/ru";

const pickerSupported = () =>
  typeof navigator !== "undefined" && !!navigator.contacts && typeof navigator.contacts.select === "function";

const iconBtn = "w-10 h-10 rounded-full flex items-center justify-center active:scale-[0.94] transition-transform shrink-0";

export default function Contacts() {
  const navigate = useNavigate();
  const { startCall } = useCall();
  const [contacts, setContacts] = useState([]);
  const [suggested, setSuggested] = useState([]);
  const [match, setMatch] = useState(null);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const searchPeople = async (e) => {
    e.preventDefault();
    if (search.trim().length < 2) { setSearchError(ru.contacts.searchMin); return; }
    setSearching(true); setSearchError(null);
    try { const { data } = await api.get("/users/search", { params: { q: search.trim() } }); setResults(data); }
    catch (err) { setSearchError(formatApiError(err, ru.errors.generic)); }
    finally { setSearching(false); }
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/contacts");
      setContacts(data.contacts);
      setSuggested(data.suggested);
    } catch {}
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const add = async (userId) => {
    setBusy(true);
    try {
      await api.post("/contacts", { user_id: userId });
      toast.success(ru.contacts.added);
      setMatch((m) =>
        m ? { ...m, found: m.found.map((f) => (f.user_id === userId ? { ...f, is_contact: true } : f)) } : m
      );
      await load();
    } catch (err) {
      toast.error(formatApiError(err, ru.errors.generic));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (userId) => {
    setBusy(true);
    try {
      await api.delete(`/contacts/${userId}`);
      toast.success(ru.contacts.removed);
      await load();
    } catch (err) {
      toast.error(formatApiError(err, ru.errors.generic));
    } finally {
      setBusy(false);
    }
  };

  const pickFromPhonebook = async () => {
    try {
      const picked = await navigator.contacts.select(["tel"], { multiple: true });
      const phones = picked.flatMap((p) => p.tel || []).slice(0, 100);
      if (phones.length === 0) return;
      const { data } = await api.post("/contacts/match", { phones });
      setMatch({ found: data.matches, total: phones.length });
    } catch (err) {
      toast.error(formatApiError(err, ru.contacts.pickerError));
    }
  };

  const shareLink = async () => {
    try {
      const { data } = await api.get("/invites/my-link");
      const payload = { title: ru.app.name, text: ru.contacts.shareText, url: data.link };
      if (navigator.share) {
        try {
          await navigator.share(payload);
          return;
        } catch {}
      }
      await navigator.clipboard.writeText(`${ru.contacts.shareText} ${data.link}`);
      toast.success(ru.contacts.linkCopied);
    } catch (err) {
      toast.error(formatApiError(err, ru.errors.generic));
    }
  };

  const onCall = (c, media = "audio") => {
    if (c.has_dm) startCall({ id: c.id, name: c.name, avatar: c.avatar }, media);
    else navigate(`/chats/u/${c.id}`);
  };

  const carLine = (c) =>
    c.car ? [c.car.model, c.car.color, c.car.plate].filter(Boolean).join(" · ") : null;

  return (
    <div className="pt-8 pb-28" data-testid="contacts-page">
      <h1 className="text-3xl font-extrabold tracking-tight px-6 mb-6">{ru.contacts.title}</h1>

      <form data-testid="people-search-form" onSubmit={searchPeople} className="px-6 mb-5 flex gap-2">
        <input data-testid="people-search-input" value={search} onChange={e => { setSearch(e.target.value); if (!e.target.value) { setResults(null); setSearchError(null); } }} maxLength={100} placeholder={ru.contacts.search} className="bg-gray-100 rounded-xl px-3 h-11 min-w-0 flex-1 outline-none focus:ring-2 focus:ring-black" />
        <button data-testid="people-search-submit" disabled={searching || search.trim().length < 2} className="bg-black text-white px-4 rounded-xl text-sm font-semibold disabled:opacity-30">{ru.contacts.searchButton}</button>
      </form>
      {searchError && <p role="alert" data-testid="people-search-error" className="px-6 mb-4 text-sm text-red-600">{searchError}</p>}
      {searching && <p data-testid="people-search-loading" className="px-6 text-sm text-gray-500">{ru.contacts.searching}</p>}
      {results && <section className="px-6 mb-6" data-testid="people-search-results"><h2 className="text-base font-bold mb-2">{ru.contacts.searchResults}</h2>{results.length === 0 && <p data-testid="people-search-empty" className="text-sm text-gray-500">{ru.contacts.searchEmpty}</p>}{results.map(person => <div key={person.id} data-testid={`people-result-${person.id}`} className="py-3 border-b border-gray-100"><div className="flex items-center gap-3"><Avatar name={person.name} avatar={person.avatar} size={40} /><div className="min-w-0 flex-1"><p className="font-semibold truncate">{person.name}</p><p className="text-xs text-gray-500 truncate">{carLine(person)}</p></div></div><div className="flex gap-2 mt-2 pl-[52px]"><button data-testid={`people-write-${person.id}`} onClick={() => navigate(`/chats/u/${person.id}`)} className="px-3 py-2 bg-gray-100 rounded-full text-xs font-semibold">{ru.contacts.write}</button>{contacts.some(c => c.id === person.id) ? <span data-testid={`people-added-${person.id}`} className="text-xs text-gray-500 py-2">{ru.contacts.inContacts}</span> : <button data-testid={`people-add-${person.id}`} disabled={busy} onClick={() => add(person.id)} className="px-3 py-2 bg-black text-white rounded-full text-xs font-semibold disabled:opacity-40">{ru.contacts.add}</button>}</div></div>)}</section>}

      {contacts.length === 0 && suggested.length === 0 && (
        <div className="px-6 mb-7" data-testid="contacts-empty">
          <p className="font-bold text-base md:text-lg">{ru.contacts.emptyTitle}</p>
          <p className="text-sm text-gray-500 mt-1.5">{ru.contacts.emptyDesc}</p>
        </div>
      )}

      {contacts.length > 0 && (
        <section className="mb-7">
          <h2 className="text-base md:text-lg font-bold px-6 mb-2">{ru.contacts.mine}</h2>
          <div data-testid="contacts-list">
            {contacts.map((c) => (
              <div key={c.id} data-testid={`contact-row-${c.id}`} className="flex items-center gap-3 px-6 py-3">
                <div className="relative shrink-0">
                  <Avatar avatar={c.avatar} name={c.name} size={44} />
                  {c.online && (
                    <span className="absolute bottom-0 right-0 w-3 h-3 bg-[#34C759] border-2 border-white rounded-full" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-bold truncate">{c.name}</p>
                  <p className="text-xs text-gray-500 truncate">
                    {carLine(c) || (c.online ? ru.chats.online : ru.chats.offline)}
                  </p>
                </div>
                <button
                  data-testid={`contact-write-${c.id}`}
                  onClick={() => navigate(`/chats/u/${c.id}`)}
                  title={ru.contacts.write}
                  className={`${iconBtn} bg-gray-100`}
                >
                  <MessageCircle size={17} />
                </button>
                <button
                  data-testid={`contact-call-${c.id}`}
                  onClick={() => onCall(c)}
                  title={ru.contacts.call}
                  className={`${iconBtn} bg-black text-white`}
                >
                  <Phone size={16} />
                </button>
                {c.mutual && (
                  <button
                    data-testid={`contact-video-call-${c.id}`}
                    onClick={() => onCall(c, "video")}
                    title={ru.calls.videoCall}
                    className={`${iconBtn} bg-black text-white`}
                  >
                    <Video size={16} />
                  </button>
                )}
                <button
                  data-testid={`contact-remove-${c.id}`}
                  disabled={busy}
                  onClick={() => remove(c.id)}
                  title={ru.contacts.remove}
                  className={`${iconBtn} bg-gray-100 text-gray-400 w-8 h-8`}
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      {suggested.length > 0 && (
        <section className="mb-7">
          <h2 className="text-base md:text-lg font-bold px-6 mb-2">{ru.contacts.suggested}</h2>
          <div data-testid="suggested-list">
            {suggested.map((s) => (
              <div key={s.id} data-testid={`suggested-row-${s.id}`} className="flex items-center gap-3 px-6 py-3">
                <Avatar avatar={s.avatar} name={s.name} size={40} />
                <div className="min-w-0 flex-1">
                  <p className="font-semibold truncate">{s.name}</p>
                  <p className="text-xs text-gray-500 truncate">{carLine(s) || ru.contacts.suggested}</p>
                </div>
                <button
                  data-testid={`suggested-add-${s.id}`}
                  disabled={busy}
                  onClick={() => add(s.id)}
                  className="shrink-0 h-9 px-3.5 rounded-full bg-black text-white text-sm font-semibold flex items-center gap-1.5 active:scale-[0.96] transition-transform disabled:opacity-40"
                >
                  <UserPlus size={14} /> {ru.contacts.add}
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      {match && (
        <section className="mb-7 px-6" data-testid="match-results">
          <h2 className="text-base md:text-lg font-bold mb-2">{ru.contacts.matchTitle}</h2>
          {match.found.length === 0 && (
            <p className="text-sm text-gray-500" data-testid="match-none">{ru.contacts.matchNone}</p>
          )}
          {match.found.map((m) => (
            <div key={m.user_id} data-testid={`match-row-${m.user_id}`} className="flex items-center gap-3 py-2.5">
              <Avatar avatar={m.avatar} name={m.name} size={40} />
              <div className="min-w-0 flex-1">
                <p className="font-semibold truncate">{m.name}</p>
                <p className="text-xs text-gray-500 truncate">{m.phone_matched}</p>
              </div>
              {m.is_contact ? (
                <span className="text-xs font-semibold text-gray-400">{ru.contacts.inContacts}</span>
              ) : (
                <button
                  data-testid={`match-add-${m.user_id}`}
                  disabled={busy}
                  onClick={() => add(m.user_id)}
                  className="shrink-0 h-9 px-3.5 rounded-full bg-black text-white text-sm font-semibold active:scale-[0.96] transition-transform disabled:opacity-40"
                >
                  {ru.contacts.add}
                </button>
              )}
            </div>
          ))}
          {match.total > match.found.length && (
            <p className="text-sm text-gray-500 mt-2" data-testid="match-rest">
              {ru.contacts.matchRest(match.total - match.found.length)}
            </p>
          )}
        </section>
      )}

      <section className="px-6 space-y-3">
        {pickerSupported() && (
          <div className="bg-gray-50 rounded-2xl p-4">
            <p className="font-semibold text-sm">{ru.contacts.findFriends}</p>
            <p className="text-xs text-gray-500 mt-1 mb-3">{ru.contacts.findFriendsDesc}</p>
            <button
              data-testid="phonebook-button"
              onClick={pickFromPhonebook}
              className="w-full h-11 bg-black text-white rounded-xl text-sm font-semibold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
            >
              <BookUser size={16} /> {ru.contacts.findFriends}
            </button>
          </div>
        )}

        <div className="bg-gray-50 rounded-2xl p-4" data-testid="invite-block">
          <p className="font-semibold text-sm">{ru.contacts.inviteTitle}</p>
          <p className="text-xs text-gray-500 mt-1 mb-3">{ru.contacts.inviteDesc}</p>
          <button
            data-testid="share-invite-button"
            onClick={shareLink}
            className="w-full h-11 bg-black text-white rounded-xl text-sm font-semibold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
          >
            <Share2 size={16} /> {ru.contacts.shareBtn}
          </button>
        </div>
      </section>
    </div>
  );
}
